<template>
  <Col v-if="showActionButtonGroup" class="mts-form-action-col" v-bind="actionColOpt">
    <div style="display: flex; align-items: flex-end; width: 100%">
      <Form.Item>
        <slot name="moreBefore"></slot>
        <MButton
          v-if="PCMode && showMoreButton"
          type="link"
          size="small"
          v-bind="moreButtonOptions"
          style="vertical-align: middle"
          @click="openMore"
        >
          {{ t(moreButtonOptions.text || 'common.moreFilters') }}
          <BasicArrow down />
        </MButton>

        <slot name="resetBefore"></slot>
        <MButton
          v-if="PCMode && showResetButton"
          type="default"
          class="mr-2"
          v-bind="resetButtonOptions"
          @click="resetAction"
        >
          {{ t(resetButtonOptions.text || 'common.resetText') }}
        </MButton>

        <slot name="submitBefore"></slot>
        <MButton
          v-if="showSubmitButton"
          :type="PCMode ? 'primary' : 'default'"
          :color="PCMode ? void 0 : 'green'"
          class="mr-2"
          v-bind="submitButtonOptions"
          @click="submitAction"
        >
          {{ t(submitButtonOptions.text || 'common.queryText') }}
        </MButton>

        <MButton
          v-if="PADMode && showMoreButton"
          v-bind="moreButtonOptions"
          class="!px-10px !border-none !align-middle !shadow-none"
          @click="openMore"
        >
          <MIcon prefix="g" icon="shaixuan" :size="20" class="opacity-50" />
        </MButton>

        <slot name="advanceBefore"></slot>
        <MButton
          v-if="showAdvancedButton && !hideAdvanceBtn"
          type="link"
          size="small"
          @click="toggleAdvanced"
        >
          {{ isAdvanced ? t('component.form.putAway') : t('component.form.unfold') }}
          <BasicArrow class="ml-1" :expand="!isAdvanced" up />
        </MButton>
        <slot name="advanceAfter"></slot>
      </Form.Item>
    </div>
  </Col>
</template>
<script lang="ts" setup>
  import { Col, Form } from 'ant-design-vue';
  import { computed, PropType } from 'vue';
  import { propTypes } from '@mts-fe/utils';
  import { BasicArrow } from '@mts/components/Basic';
  import { MButton, MButtonProps } from '@mts/components/Button';
  import { MIcon } from '@mts/components/Icon';
  import { t } from '@mts/hooks/web/useI18n';
  import { PADMode, PCMode } from '@mts/utils/env';
  import { useFormContext } from '../hooks/useFormContext';
  import type { ColEx } from '../types';

  defineOptions({ name: 'BasicFormAction', inheritAttrs: false });

  const props = defineProps({
    showActionButtonGroup: propTypes.bool.def(true),
    showResetButton: propTypes.bool.def(true),
    showSubmitButton: propTypes.bool.def(true),
    showAdvancedButton: propTypes.bool.def(true),
    showMoreButton: propTypes.bool.def(false),
    resetButtonOptions: {
      type: Object as PropType<MButtonProps>,
      default: () => ({}),
    },
    submitButtonOptions: {
      type: Object as PropType<MButtonProps>,
      default: () => ({}),
    },
    moreButtonOptions: {
      type: Object as PropType<MButtonProps>,
      default: () => ({}),
    },
    actionColOptions: {
      type: Object as PropType<Partial<ColEx>>,
      default: () => ({}),
    },
    actionSpan: propTypes.number.def(6),
    isAdvanced: propTypes.bool,
    hideAdvanceBtn: propTypes.bool,
  });

  const emit = defineEmits(['open-more', 'toggle-advanced']);

  const { resetAction, submitAction } = useFormContext();

  const actionColOpt = computed(() => {
    const { showAdvancedButton, actionSpan: span, actionColOptions } = props;
    const actionSpan = 24 - span;
    const advancedSpanObj = showAdvancedButton ? { span: actionSpan < 6 ? 24 : actionSpan } : {};
    const actionColOpt: Partial<ColEx> = {
      span: showAdvancedButton ? 6 : 4,
      ...advancedSpanObj,
      ...actionColOptions,
    };
    return actionColOpt;
  });

  function openMore() {
    emit('open-more');
  }

  function toggleAdvanced() {
    emit('toggle-advanced');
  }
</script>

<style lang="less">
  .mts-basic-form .mts-form-action-col {
    display: flex;
    justify-content: flex-end;

    .ant-form-item-control-input-content {
      display: flex;
      align-items: center;
    }
  }
</style>
